// A PRO video is one job item: the worker tracks it and reports each step as rings in
// frame pixels (docs/spatial-vision/2026-09-21-video-processor-plan.md, Task 7).

const PALETTE = [
  '#2563EB',
  '#16A34A',
  '#EA580C',
  '#9333EA',
  '#0891B2',
  '#DB2777',
  '#CA8A04',
  '#4F46E5',
  '#059669',
  '#DC2626',
];
export const UNLINKED_COLOR = '#9CA3AF';

const naturalOrder = (a, b) =>
  String(a).localeCompare(String(b), undefined, { numeric: true });

export const isVideoFile = file =>
  String(file?.type || '').startsWith('video/');

// PRO sends the whole file and the worker counts each thing once; CLOUD has no tracker,
// so a video there is still cut into frames in the browser.
export const samplesVideo = (file, serviceTier) =>
  isVideoFile(file) && serviceTier !== 'pro';

export const isVideoItem = item =>
  item?.resultMetadata?.kind === 'video' || isVideoFile(item?.file);

export function stepAtTime(steps, ms) {
  let found = null;
  for (const step of steps || []) {
    if (step.timestampMs > ms) break;
    found = step;
  }
  return found;
}

export const firstStepOf = (steps, trackId) =>
  (steps || []).find(step =>
    step.tracks.some(track => track.trackId === trackId),
  ) || null;

export function trackColor(trackId) {
  const text = String(trackId);
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) {
    hash = (hash * 31 + text.charCodeAt(index)) >>> 0;
  }
  return PALETTE[hash % PALETTE.length];
}

// The worker names things "the apple"; a label beside an ID reads better without it.
export const nounLabel = noun => String(noun || '').replace(/^the\s+/i, '');

// "cherry" -> "cherries", "box" -> "boxes"; a class is called what the operator called it
export function plural(label, count) {
  if (count === 1) return label;
  if (/[^aeiou]y$/.test(label)) return `${label.slice(0, -1)}ies`;
  if (/(s|sh|ch|x|z)$/.test(label)) return `${label}es`;
  return `${label}s`;
}

const capitalized = text => text.charAt(0).toUpperCase() + text.slice(1);

const kindsList = byKind =>
  Object.keys(byKind || {})
    .map(kind => ({
      kind: nounLabel(kind),
      ids: [...byKind[kind]].sort(naturalOrder),
    }))
    .sort((a, b) => naturalOrder(a.kind, b.kind));

// One tile a class the request named, not a fixed "Objects" and "Defects": a request for
// apples has no defects to show as 0, and one for cherries, apples and cars has three counts.
// The list shows the things something was found inside, with what was found; everything that
// is inside nothing and holds nothing goes under "Independent objects" by class, flaws on no
// thing included (Yeskendir, 2026-09-29).
export function panelModel(summary) {
  const objectClass = summary?.objectClass || {};
  const perObject = summary?.perObject || {};
  const flawClass = (summary?.classes || []).find(cls => cls.general)?.key;
  const independent = {};
  Object.keys(perObject)
    .filter(trackId => !Object.keys(perObject[trackId]).length)
    .forEach(trackId => {
      const cls = objectClass[trackId] || '';
      independent[cls] = [...(independent[cls] || []), trackId];
    });
  Object.entries(summary?.unlinked || {}).forEach(([kind, ids]) => {
    const cls = flawClass || kind;
    independent[cls] = [...(independent[cls] || []), ...ids];
  });
  return {
    tiles: (summary?.classes || []).map(cls => ({
      label: capitalized(plural(cls.key, cls.count)),
      count: cls.count,
    })),
    note: summary?.note || null,
    items: Object.keys(perObject)
      .filter(trackId => Object.keys(perObject[trackId]).length)
      .sort(naturalOrder)
      .map(trackId => ({
        trackId,
        noun: nounLabel(objectClass[trackId] || ''),
        kinds: kindsList(perObject[trackId]),
      })),
    independent: kindsList(independent),
  };
}

export function progressText(videoProgress) {
  if (!videoProgress) return null;
  const { step, steps, counts = {} } = videoProgress;
  const byClass = Object.entries(counts.byClass || {}).map(
    ([key, count]) => `${count} ${plural(key, count)}`,
  );
  return [`Tracking step ${step} of ${steps}`, byClass.join(', ')]
    .filter(Boolean)
    .join(' · ');
}

// While a video runs the worker reports its latest step; the final playback does not exist yet,
// so that step is drawn over the uploaded file, whose timeline is the file's own.
export function liveStep(videoProgress) {
  const latest = videoProgress?.latest;
  return latest ? { ...latest, timestampMs: latest.sourceMs } : null;
}
